/**
 * Navigation Guard
 * Prevents navigation throttling by intercepting and managing all navigation calls
 */

class NavigationGuard {
  constructor() {
    this.isInitialized = false;
    this.navigationCount = 0;
    this.lastNavigationTime = 0;
    this.throttleThreshold = 100; // 100ms minimum between navigations (much more lenient)
    this.maxNavigationsPerMinute = 60; // Allow up to 60 navigations per minute
    this.navigationHistory = [];
  }

  /**
   * Initialize the navigation guard
   */
  init() {
    if (this.isInitialized) return;
    
    // Override window.history methods to prevent rapid navigation
    this.overrideHistoryMethods();
    
    // Skip location method overrides as they're read-only in modern browsers
    // React Router uses history API which we're already protecting
    
    this.isInitialized = true;
    // Navigation guard initialized
  }

  /**
   * Override history methods to add throttling
   */
  overrideHistoryMethods() {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    const originalBack = history.back;
    const originalForward = history.forward;
    const originalGo = history.go;

    history.pushState = (...args) => {
      if (this.shouldThrottleNavigation('pushState')) {
        console.warn('Navigation throttled: pushState');
        return;
      }
      this.recordNavigation('pushState');
      return originalPushState.apply(history, args);
    };

    history.replaceState = (...args) => {
      if (this.shouldThrottleNavigation('replaceState')) {
        console.warn('Navigation throttled: replaceState');
        return;
      }
      this.recordNavigation('replaceState');
      return originalReplaceState.apply(history, args);
    };

    history.back = () => {
      if (this.shouldThrottleNavigation('back')) {
        console.warn('Navigation throttled: back');
        return;
      }
      this.recordNavigation('back');
      return originalBack.apply(history);
    };

    history.forward = () => {
      if (this.shouldThrottleNavigation('forward')) {
        console.warn('Navigation throttled: forward');
        return;
      }
      this.recordNavigation('forward');
      return originalForward.apply(history);
    };

    history.go = (...args) => {
      if (this.shouldThrottleNavigation('go')) {
        console.warn('Navigation throttled: go');
        return;
      }
      this.recordNavigation('go');
      return originalGo.apply(history, args);
    };
  }

  /**
   * Override location methods to add throttling
   * Note: location.assign and location.replace are read-only in modern browsers
   * So we'll use a different approach - intercepting navigation calls
   */
  overrideLocationMethods() {
    // Store original methods
    this.originalAssign = window.location.assign.bind(window.location);
    this.originalReplace = window.location.replace.bind(window.location);
    
    // Create wrapper functions that can be called instead
    window.safeLocationAssign = (url) => {
      if (this.shouldThrottleNavigation('assign')) {
        console.warn('Navigation throttled: assign');
        return;
      }
      this.recordNavigation('assign');
      return this.originalAssign(url);
    };

    window.safeLocationReplace = (url) => {
      if (this.shouldThrottleNavigation('replace')) {
        console.warn('Navigation throttled: replace');
        return;
      }
      this.recordNavigation('replace');
      return this.originalReplace(url);
    };
  }

  /**
   * Check if navigation should be throttled
   * DISABLED: We only need to prevent browser throttling, not add our own
   */
  shouldThrottleNavigation(method) {
    // Disable throttling - we only need to prevent browser throttling
    return false;
  }

  /**
   * Record a navigation event
   */
  recordNavigation(method) {
    const now = Date.now();
    this.lastNavigationTime = now;
    this.navigationCount++;
    
    this.navigationHistory.push({
      method,
      timestamp: now,
      count: this.navigationCount
    });

    // Clean up old history (keep only last 100 entries)
    if (this.navigationHistory.length > 100) {
      this.navigationHistory = this.navigationHistory.slice(-100);
    }
  }

  /**
   * Get navigation statistics
   */
  getStats() {
    return {
      totalNavigations: this.navigationCount,
      lastNavigationTime: this.lastNavigationTime,
      recentNavigations: this.navigationHistory.slice(-10),
      isThrottling: this.shouldThrottleNavigation('check')
    };
  }

  /**
   * Reset navigation guard
   */
  reset() {
    this.navigationCount = 0;
    this.lastNavigationTime = 0;
    this.navigationHistory = [];
  }
}

// Create singleton instance
const navigationGuard = new NavigationGuard();

export default navigationGuard;
